import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { uriNameOfEntityOrEmpty } from '@briebug/ngrx-auto-entity';
import { Store } from '@ngrx/store';
import { catchError, finalize, of, take } from 'rxjs';
import { AppUIService } from '../../../domains/app-ui.service';
import { RetainageService, RetainageSummary } from '../../../domains/retainage/retainage.service';
import { loadAllWorkOrderItems } from '../../../domains/work/work-order-item/work-order-item.state';
import { WorkOrder } from '../../../domains/work/work-order/work-order.model';
import { loadWorkOrder } from '../../../domains/work/work-order/work-order.state';

/**
 * TT-260. Under the Items totals: the retainage % held back on this work
 * order, how much is held so far, and, for the company once every other line
 * and change order is invoiced and approved, the Release button. After
 * release the installer bills the new Retainage line on its own invoice.
 * Shows nothing on a work order without retainage.
 */
@Component({
  selector: 'gc-work-order-retainage',
  template: `
    <ion-list *ngIf="summary?.percent" lines="none" class="ion-no-padding retainage" data-cy="work-order-retainage">
      <ion-item class="ion-no-padding">
        <ion-label class="ion-text-end">Retainage ({{ summary!.percent }}%) held to date</ion-label>
        <strong slot="end" data-cy="retainage-held">{{ summary!.held | currency }}</strong>
      </ion-item>
      <ion-item *ngIf="summary!.releasedAt" class="ion-no-padding">
        <ion-label class="ion-text-end" color="success">
          Released {{ summary!.releasedAt | date: 'mediumDate' }}
          <ng-container *ngIf="!isCompany">: invoice the Retainage line on its own</ng-container>
        </ion-label>
      </ion-item>
      <ion-item *ngIf="!summary!.releasedAt && !summary!.readyToRelease" class="ion-no-padding">
        <ion-label class="ion-text-end ion-text-wrap" color="medium">
          Held back from each invoice until {{ isCompany ? 'you release it' : 'the company releases it' }}, once every
          other line and change order is invoiced and approved.
        </ion-label>
      </ion-item>
      <ion-item *ngIf="!summary!.releasedAt && summary!.readyToRelease" class="ion-no-padding">
        <ion-label class="ion-text-end ion-text-wrap" color="medium">
          {{ isCompany ? 'Everything else is invoiced and approved.' : 'Ready for the company to release.' }}
        </ion-label>
        <ion-button
          *ngIf="isCompany"
          slot="end"
          size="small"
          [disabled]="releasing"
          (click)="release()"
          data-cy="retainage-release-button"
        >
          Release {{ summary!.held | currency }}
        </ion-button>
      </ion-item>
    </ion-list>
  `,
  styles: [
    `
      .retainage ion-item {
        --min-height: 28px;
        font-size: 14px;
      }
    `,
  ],
})
export class WorkOrderRetainageComponent implements OnChanges {
  @Input() workOrderId?: string;
  @Input() isCompany = false;

  summary?: RetainageSummary;
  releasing = false;

  constructor(
    private readonly retainage: RetainageService,
    private readonly appUI: AppUIService,
    private readonly store: Store,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workOrderId'] && this.workOrderId) {
      this.load();
    }
  }

  private load(): void {
    if (!this.workOrderId) return;
    this.retainage
      .summary(this.workOrderId)
      .pipe(
        take(1),
        catchError(() => of(undefined)),
      )
      .subscribe(summary => (this.summary = summary));
  }

  async release(): Promise<void> {
    const workOrderId = this.workOrderId;
    if (!workOrderId || !this.summary) return;
    const amount = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(this.summary.held);
    const confirmed = await this.appUI.confirm(
      'Release the retainage?',
      `This adds a ${amount} Retainage line the installer can invoice on its own, paid like any other invoice with its own lien release. It can't be undone.`,
      { confirmText: 'Release', cssClass: 'ion-color-primary' },
    );
    if (!confirmed) return;
    this.releasing = true;
    this.retainage
      .release(workOrderId)
      .pipe(finalize(() => (this.releasing = false)))
      .subscribe({
        next: () => {
          void this.appUI.toastResult(`Retainage released: the installer can now invoice ${amount}.`);
          this.load();
          this.store.dispatch(loadWorkOrder({ keys: workOrderId }));
          this.store.dispatch(
            loadAllWorkOrderItems({ criteria: { parents: { [uriNameOfEntityOrEmpty(WorkOrder)]: workOrderId } } }),
          );
        },
        error: err => {
          const message = err?.error?.message ?? "The retainage couldn't be released. Please try again.";
          void this.appUI.toastResult(message, '', { duration: 6000, error: true });
        },
      });
  }
}
